import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import {
  TemplatePreviewCardComponent,
  TemplateData,
} from './components/template-preview-card/template-preview-card.component';
import { TemplateService, TemplateData as ApiTemplateData } from './services/template.service';
import { LocationService } from '../services/location.service';
import { NotificationService } from '../services/notification.service'; // NotificationService;

/**
 * CarWashWebsiteComponent - Manages the Car Wash website creation and management UI, allowing users to view or create a website from templates, preview templates, and open the website builder/editor.
 * @component
 * @selector app-car-wash-website
 * @example
 *   <app-car-wash-website></app-car-wash-website>
 * @input none - This component does not accept any @Input properties.
 * @output none - This component does not emit any @Output events.
 *
 * Purpose:
 * - Provides a dashboard for car wash website owners to either view their existing website or browse and create a website from available templates.
 * - Coordinates fetching templates and existing website data, manages UI state (loading, errors, modals, menu), and launches external pages (preview, builder).
 *
 * Key state properties:
 * - templates: TemplateData[] - List of available templates transformed for display.
 * - loading: boolean - Global loading state while templates are being fetched.
 * - loadingWebsite: boolean - Loading state for fetching existing website details.
 * - error: string | null - Error message when template loading fails.
 * - websiteId: string | null - Current website identifier (from API or sessionStorage).
 * - createdTemplateId: string | null - The template id used to create the current website.
 * - existingWebsite: any | null - Raw website data returned by the API when a website exists.
 * - activeTab: 'your-website' | 'browse-templates' - Currently selected tab.
 * - showMenu: boolean - Whether the action menu is visible.
 * - showResetModal: boolean - Whether the reset confirmation modal is visible.
 * - resetConfirmed: boolean - Whether the user confirmed the reset action.
 * - resettingWebsite: boolean - Loading state while a reset operation is in progress.
 * - loadingTemplateId: string | null - Id of the template currently being used to create a website (per-item loading).
 *
 * Dependencies:
 * - TemplateService: Used to fetch templates and website details, create websites, and reset websites.
 * - Uses sessionStorage to persist websiteId and templateId when available.
 * - Uses window.open to launch preview and builder pages and window.location.reload on reset.
 *
 * Lifecycle hooks:
 * - ngOnInit(): On initialization, attempts to read websiteId and templateId from sessionStorage and then fetches existing website data via checkExistingWebsite(). The logic prefers session-stored IDs to determine which tab to show but still validates by fetching full data.
 *
 * Important methods:
 * - checkExistingWebsite(): Initiates an API call to retrieve the current website. Sets loadingWebsite and forwards the response to applyGetWebsiteResponse. On error, treats response as null to fall back to browsing templates.
 * - applyGetWebsiteResponse(resp: any, onNull?: () => void): Updates component state from getWebsite() response. Sets existingWebsite, websiteId, createdTemplateId and switches activeTab to 'your-website' when valid; otherwise clears existingWebsite, switches to 'browse-templates', and calls onNull (commonly used to load templates).
 * - loadTemplates(): Fetches templates via TemplateService, handles loading/error states, sorts templates by order_number when present, and maps API payload to the TemplateData shape used in the UI. Sets a default pages count and a descriptive preview text.
 * - onTemplateSelect(template: TemplateData): Creates a website from the selected template. Sets per-template loading, calls createWebsite(), persists websiteId/templateId to sessionStorage when returned, refreshes website data, opens the website builder in a new tab, and handles error fallback (opens builder with templateId only).
 * - refreshWebsiteData(): Re-fetches website data and applies the response via applyGetWebsiteResponse. Handles and logs errors when refresh fails.
 * - isTemplateLoading(template: TemplateData): boolean - Helper to tell whether a given template is currently in a loading/creation state.
 * - onTemplatePreview(template: TemplateData): Opens a preview tab for a template.
 * - onEditExisting(): If an existing website exists, opens the website editor for that website in a new tab (passes websiteId).
 * - setActiveTab(tab: 'your-website' | 'browse-templates'): Switches active tab and triggers loadTemplates() when switching to 'browse-templates' and templates are not yet loaded.
 * - onReset(): Prepares and opens the reset confirmation modal (hides menu and shows modal).
 * - closeResetModal(): Closes the reset modal and clears confirmation state.
 * - confirmReset(): If resetConfirmed and websiteId are present, calls TemplateService.resetWebsite(), manages resettingWebsite state, reloads the page on success, and alerts/logs on error.
 * - toggleMenu(): Toggles the action menu visibility.
 * - closeMenuOnOutsideClick(event: Event): Closes the menu when a click occurs outside the menu or menu button.
 *
 * Error handling and UX considerations:
 * - Gracefully handles sessionStorage access errors with try/catch.
 * - Provides per-template and global loading flags to prevent duplicate actions and show appropriate UI states.
 * - Logs API errors to the console and shows user-facing messages where appropriate (alerts for reset failures, error strings for template load failures).
 * - Ensures operations that change the application context (create, edit, preview, reset) open external pages or reload the application as needed.
 */
@Component({
  selector: 'app-car-wash-website',
  standalone: true,
  imports: [CommonModule, FormsModule, TitleBarComponent, TemplatePreviewCardComponent],
  templateUrl: './car-wash-website.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./car-wash-website.component.scss'],
})
export class CarWashWebsiteComponent implements OnInit {
  templates: TemplateData[] = [];
  loading = true;
  loadingWebsite = false;
  error: string | null = null;
  websiteId: string | null = null;
  createdTemplateId: string | null = null;
  existingWebsite: any | null = null;
  activeTab: 'your-website' | 'browse-templates' = 'your-website';
  showMenu = false;
  showResetModal = false;
  resetConfirmed = false;
  resettingWebsite = false;
  loadingTemplateId: string | null = null;

  constructor(
    private templateService: TemplateService,
    private locationService: LocationService,
    private messageService: NotificationService
  ) {}

  /**
   * Initialize component state on creation, preferring session-stored IDs and ensuring website data is fetched.
   * @example
   * gOnInit()
   * // No return value (void)
   * @returns {void} Does not return a value; updates component properties and triggers fetching of website data.
   **/
  ngOnInit(): void {
    this.locationService.clearShopLocation();

    // Prefer session-stored IDs if available
    let storedWebsiteId: string | null = null;
    let storedTemplateId: string | null = null;
    try {
      storedWebsiteId = sessionStorage.getItem('websiteId');
      storedTemplateId = sessionStorage.getItem('templateId');
    } catch {
      /* ignore */
    }

    // Case 1: Only websiteId present → still fetch full data to show proper details
    if (storedWebsiteId && !storedTemplateId) {
      this.websiteId = storedWebsiteId;
      this.activeTab = 'your-website';
      // Still fetch website data to get full details
      this.checkExistingWebsite();
      return;
    }

    // Case 2: Both IDs present → do NOT bypass; fetch full data
    // Case 3: No IDs → fetch as usual
    this.checkExistingWebsite();
  }

  private checkExistingWebsite(): void {
    this.loadingWebsite = true;
    this.templateService.getWebsite().subscribe({
      next: resp => this.applyGetWebsiteResponse(resp, () => this.loadTemplates()),
      error: () => {
        this.applyGetWebsiteResponse(null, () => this.loadTemplates());
      },
    });
  }

  /**
   * Apply a getWebsite() API response to component state.
   * Sets existingWebsite, websiteId, createdTemplateId, and activeTab.
   * Calls onNull() when the response has no valid data (null/error cases).
   */
  private applyGetWebsiteResponse(resp: any, onNull?: () => void): void {
    this.loadingWebsite = false;
    if (resp?.status === true && resp?.data) {
      this.existingWebsite = resp.data;
      this.websiteId = resp.data.id;
      this.createdTemplateId = resp.data.template_id;
      this.activeTab = 'your-website';
    } else {
      this.existingWebsite = null;
      this.activeTab = 'browse-templates';
      onNull?.();
    }
  }

  /**
   * Loads templates from the API, sorts by order_number, transforms them for the component, and updates loading/error state.
   * @example
   * oadTemplates()
   * undefined
   * @param {void} none - No arguments.
   * @returns {void} Does not return a value; updates component properties (templates, loading, error).
   */
  loadTemplates(): void {
    this.loading = true;
    this.error = null;

    this.templateService.getTemplates().subscribe({
      next: response => {
        if (response.status && response.data) {
          // Sort by order_number if available, otherwise maintain original order
          const sortedData = Array.isArray(response.data)
            ? [...response.data].sort((a: any, b: any) => {
                const orderA = a.order_number ?? Number.MAX_SAFE_INTEGER;
                const orderB = b.order_number ?? Number.MAX_SAFE_INTEGER;
                return orderA - orderB;
              })
            : response.data;

          // Transform API data to match component interface
          this.templates = sortedData.map((apiTemplate: ApiTemplateData) => ({
            id: apiTemplate.id,
            name: apiTemplate.name,
            pages: 6, // Default pages since API doesn't provide this
            preview: apiTemplate.thumbnail_url,
            description: `Clean and modern design perfect for car wash businesses`,
          }));
        } else {
          this.error = response.message || 'Failed to load templates';
        }
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load templates. Please try again.';
        this.loading = false;
        console.error('Error loading templates:', err);
      },
    });
  }

  /**
   * Selects a template, creates a website from it via the templateService, updates component state and opens the website builder in a new tab.
   * @example
   * nTemplateSelect({ id: 'tpl123', name: 'Basic Template' })
   * void
   * @param {{TemplateData}} {{template}} - Template object (must include id and name) used to create the website.
   * @returns {{void}} No return value; performs side effects including network request, sessionStorage updates, state changes and opening a new tab.
   **/
  onTemplateSelect(template: TemplateData): void {
    console.log('Template selected:', template);

    // Set loading state for this template
    this.loadingTemplateId = template.id;

    const request = { name: template.name, template_id: template.id };
    this.templateService.createWebsite(request).subscribe({
      next: response => {
        console.log('Website created successfully:', response);
        const data: any = (response as any)?.data || {};
        const idFromResponse = data.website_id || data.id || '';
        const tplIdFromResponse = data.template_id || template.id;

        if (idFromResponse) this.websiteId = idFromResponse;
        if (tplIdFromResponse) this.createdTemplateId = tplIdFromResponse;

        // Store in session storage
        if (idFromResponse) {
          try {
            sessionStorage.setItem('websiteId', idFromResponse);
          } catch {
            /* ignore */
          }
        }
        if (tplIdFromResponse) {
          try {
            sessionStorage.setItem('templateId', tplIdFromResponse);
          } catch {
            /* ignore */
          }
        }

        // Clear loading state
        this.loadingTemplateId = null;

        // Refresh the current tab to show the newly created website
        this.refreshWebsiteData();

        // Open website builder in new tab
        const url = `/website-builder?templateId=${tplIdFromResponse}${idFromResponse ? `&websiteId=${idFromResponse}` : ''}`;
        window.open(url, '_blank');
      },
      error: error => {
        console.error('Error creating website:', error);
        // Clear loading state
        this.loadingTemplateId = null;

        // Fallback: navigate with templateId only
        window.open(`/website-builder?templateId=${template.id}`, '_blank');
      },
    });
  }

  private refreshWebsiteData(): void {
    this.loadingWebsite = true;
    this.templateService.getWebsite().subscribe({
      next: resp => this.applyGetWebsiteResponse(resp),
      error: () => {
        this.loadingWebsite = false;
        console.error('Error refreshing website data');
      },
    });
  }

  isTemplateLoading(template: TemplateData): boolean {
    return this.loadingTemplateId === template.id;
  }

  get isWebsitePublished(): boolean {
    return !!this.existingWebsite?.publish;
  }

  onTemplatePreview(template: TemplateData): void {
    console.log('Template preview:', template);
    // Open preview in new tab
    window.open(`/preview?id=${template.id}`, '_blank');
  }

  // Open editor for existing website
  onEditExisting(): void {
    if (!this.existingWebsite) return;
    const wId = this.existingWebsite.id;
    // Pass only websiteId; editor will fetch details and derive templateId
    window.open(`/website-builder?websiteId=${wId}`, '_blank');
  }

  setActiveTab(tab: 'your-website' | 'browse-templates'): void {
    this.activeTab = tab;

    if (tab === 'your-website' && this.websiteId) {
      this.refreshWebsiteData();
    }

    // Load templates when switching to browse-templates tab
    if (tab === 'browse-templates' && this.templates.length === 0) {
      this.loadTemplates();
    }
  }

  onReset(): void {
    this.showMenu = false;
    this.showResetModal = true;
  }

  closeResetModal(): void {
    this.showResetModal = false;
    this.resetConfirmed = false;
  }

  /**
   * Reset the current website to its default state after user confirmation; reloads the page on success and shows an alert on error.
   * @example
   * onfirmReset()
   * undefined
   * @returns {void} Does not return a value.
   */
  confirmReset(): void {
    if (!this.resetConfirmed || !this.websiteId) return;

    this.resettingWebsite = true;

    this.templateService.resetWebsite(this.websiteId).subscribe({
      next: response => {
        console.log('Website reset successfully:', response);
        this.resettingWebsite = false;
        this.showResetModal = false;
        this.resetConfirmed = false;

        // Reload the page to show the reset website
        window.location.reload();
      },
      error: error => {
        console.error('Error resetting website:', error);
        this.resettingWebsite = false;

        // Show error message to user
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reset website. Please try again.',
        });
      },
    });
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  closeMenuOnOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.btn-menu') && !target.closest('.dropdown-menu')) {
      this.showMenu = false;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
